import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useCategoryModal, useResourceModal } from "@/hooks/useDownloadsModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  ExternalLink,
  FileText,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ConfirmModal } from "../ui/confirm-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { CategoryModalForm } from "./category-modal-form";
import { ResourceModalForm } from "./resource-modal-form";

const categoryFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  color: z.string().default("bg-blue-500"),
  sortOrder: z.number().default(0),
});

const resourceFormSchema = z.object({
  categoryId: z.string().min(1, "Category is required"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  fileType: z.string().default("PDF"),
  fileSize: z.string().optional(),
  isPublished: z.boolean().default(true),
  sortOrder: z.number().default(0),
  mediaId: z.string().optional(),
  externalUrl: z.string().optional(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;
type ResourceFormValues = z.infer<typeof resourceFormSchema>;

export function DownloadsTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [deleteType, setDeleteType] = useState<"category" | "resource" | null>(
    null,
  );

  const { data: categories = [], isLoading: categoriesLoading } = useQuery(
    trpc.downloads.categories.list.queryOptions(),
  );

  const { data: resources = [], isLoading: resourcesLoading } = useQuery(
    trpc.downloads.resources.list.queryOptions(),
  );

  const deleteId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);

  // Category modal state
  const isCategoryModalOpen = useCategoryModal((s) => s.isOpen);
  const setIsCategoryModalOpen = useCategoryModal((s) => s.setIsModalOpen);
  const categoryId = useCategoryModal((s) => s.categoryId);
  const setCategoryId = useCategoryModal((s) => s.setCategoryId);
  const categoryDefaults = useCategoryModal((s) => s.defaultValues);
  const setCategoryDefaults = useCategoryModal((s) => s.setDefaultValues);

  // Resource modal state
  const isResourceModalOpen = useResourceModal((s) => s.isOpen);
  const setIsResourceModalOpen = useResourceModal((s) => s.setIsModalOpen);
  const resourceId = useResourceModal((s) => s.resourceId);
  const setResourceId = useResourceModal((s) => s.setResourceId);
  const resourceDefaults = useResourceModal((s) => s.defaultValues);
  const setResourceDefaults = useResourceModal((s) => s.setDefaultValues);

  const categoryForm = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: "",
      description: "",
      color: "bg-blue-500",
      sortOrder: 0,
    },
  });

  const resourceForm = useForm<ResourceFormValues>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues: {
      categoryId: "",
      name: "",
      description: "",
      fileType: "PDF",
      fileSize: "",
      isPublished: true,
      sortOrder: 0,
      mediaId: undefined,
      externalUrl: "",
    },
  });

  useEffect(() => {
    if (categoryDefaults && Object.keys(categoryDefaults).length > 0) {
      categoryForm.reset({
        name: categoryDefaults.name || "",
        description: categoryDefaults.description || "",
        color: categoryDefaults.color || "bg-blue-500",
        sortOrder: categoryDefaults.sortOrder || 0,
      });
    }
  }, [categoryForm, categoryDefaults]);

  useEffect(() => {
    if (resourceDefaults && Object.keys(resourceDefaults).length > 0) {
      resourceForm.reset({
        categoryId: resourceDefaults.categoryId || "",
        name: resourceDefaults.name || "",
        description: resourceDefaults.description || "",
        fileType: resourceDefaults.fileType || "PDF",
        fileSize: resourceDefaults.fileSize || "",
        isPublished: resourceDefaults.isPublished ?? true,
        sortOrder: resourceDefaults.sortOrder || 0,
        mediaId: resourceDefaults.mediaId,
        externalUrl: resourceDefaults.externalUrl || "",
      });
    }
  }, [resourceForm, resourceDefaults]);

  // Category mutations
  const createCategoryMutation = useMutation(
    trpc.downloads.categories.create.mutationOptions({
      onSuccess: () => {
        categoryForm.reset();
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setCategoryId(null);
        toast.success("Category created successfully");
        setIsCategoryModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create category");
      },
    }),
  );

  const updateCategoryMutation = useMutation(
    trpc.downloads.categories.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setCategoryId(null);
        setIsCategoryModalOpen(false);
        categoryForm.reset();
        toast.success("Category updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update category");
      },
    }),
  );

  const deleteCategoryMutation = useMutation(
    trpc.downloads.categories.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setDeleteId(null);
        setDeleteType(null);
        toast.success("Category deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete category");
      },
    }),
  );

  // Resource mutations
  const createResourceMutation = useMutation(
    trpc.downloads.resources.create.mutationOptions({
      onSuccess: () => {
        resourceForm.reset();
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setResourceId(null);
        toast.success("Resource created successfully");
        setIsResourceModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create resource");
      },
    }),
  );

  const updateResourceMutation = useMutation(
    trpc.downloads.resources.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setResourceId(null);
        setIsResourceModalOpen(false);
        resourceForm.reset();
        toast.success("Resource updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update resource");
      },
    }),
  );

  const deleteResourceMutation = useMutation(
    trpc.downloads.resources.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["downloads"] });
        setDeleteId(null);
        setDeleteType(null);
        toast.success("Resource deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete resource");
      },
    }),
  );

  const handleCategorySubmit = (data: CategoryFormValues) => {
    if (categoryId) {
      updateCategoryMutation.mutate({ ...data, id: categoryId });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  const handleResourceSubmit = (data: ResourceFormValues) => {
    if (resourceId) {
      updateResourceMutation.mutate({ ...data, id: resourceId });
    } else {
      createResourceMutation.mutate(data);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteId || !deleteType) return;
    if (deleteType === "category") {
      deleteCategoryMutation.mutate({ id: deleteId });
    } else {
      deleteResourceMutation.mutate({ id: deleteId });
    }
  };

  const openNewCategoryModal = () => {
    setCategoryDefaults({});
    categoryForm.reset({
      name: "",
      description: "",
      color: "bg-blue-500",
      sortOrder: 0,
    });
    setCategoryId(null);
    setIsCategoryModalOpen(true);
  };

  const openNewResourceModal = (categoryIdDefault?: string) => {
    setResourceDefaults({
      categoryId: categoryIdDefault || categories[0]?.id || "",
    });
    resourceForm.reset({
      categoryId: categoryIdDefault || categories[0]?.id || "",
      name: "",
      description: "",
      fileType: "PDF",
      fileSize: "",
      isPublished: true,
      sortOrder: 0,
      mediaId: undefined,
      externalUrl: "",
    });
    setResourceId(null);
    setIsResourceModalOpen(true);
  };

  const openEditCategoryModal = (cat: (typeof categories)[0]) => {
    setCategoryId(cat.id);
    setCategoryDefaults({
      name: cat.name,
      description: cat.description || "",
      color: cat.color,
      sortOrder: cat.sortOrder,
    });
    setIsCategoryModalOpen(true);
  };

  const openEditResourceModal = (res: {
    id: string;
    categoryId: string;
    name: string;
    description: string | null;
    fileType: string;
    fileSize: string | null;
    isPublished: boolean;
    sortOrder: number;
    mediaId: string | null;
    externalUrl: string | null;
  }) => {
    setResourceId(res.id);
    setResourceDefaults({
      categoryId: res.categoryId,
      name: res.name,
      description: res.description || "",
      fileType: res.fileType,
      fileSize: res.fileSize || "",
      isPublished: res.isPublished,
      sortOrder: res.sortOrder,
      mediaId: res.mediaId || undefined,
      externalUrl: res.externalUrl || "",
    });
    setIsResourceModalOpen(true);
  };

  const isLoading = categoriesLoading || resourcesLoading;

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Downloadable Resources
            </h2>
            <p className="text-muted-foreground">
              Manage city forms, documents, and downloads for the website
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={openNewCategoryModal}>
              <FolderPlus className="mr-2 h-4 w-4" />
              New Category
            </Button>
            <Button
              onClick={() => openNewResourceModal()}
              disabled={categories.length === 0}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Resource
            </Button>
          </div>
        </div>

        <CategoryModalForm
          isModalOpen={isCategoryModalOpen}
          setIsModalOpen={setIsCategoryModalOpen}
          form={categoryForm}
          onSubmit={handleCategorySubmit}
          isLoading={
            createCategoryMutation.isPending || updateCategoryMutation.isPending
          }
        />

        <ResourceModalForm
          isModalOpen={isResourceModalOpen}
          setIsModalOpen={setIsResourceModalOpen}
          form={resourceForm}
          onSubmit={handleResourceSubmit}
          isLoading={
            createResourceMutation.isPending || updateResourceMutation.isPending
          }
          categories={categories.map((c) => ({ id: c.id, name: c.name }))}
        />

        <ConfirmModal
          open={Boolean(deleteId && deleteType)}
          onConfirm={handleDeleteConfirm}
          onDismiss={() => {
            setDeleteId(null);
            setDeleteType(null);
          }}
          isLoading={
            deleteCategoryMutation.isPending || deleteResourceMutation.isPending
          }
        />

        {!isLoading && (
          <Tabs defaultValue="by-category" className="w-full">
            <TabsList>
              <TabsTrigger value="by-category">By Category</TabsTrigger>
              <TabsTrigger value="all-resources">All Resources</TabsTrigger>
            </TabsList>

            <TabsContent value="by-category" className="space-y-4">
              {categories.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FolderPlus className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      No Categories Yet
                    </h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Create a category to start organizing your downloadable
                      resources.
                    </p>
                    <Button onClick={openNewCategoryModal}>
                      <FolderPlus className="mr-2 h-4 w-4" />
                      Create First Category
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                categories.map((category) => (
                  <Card key={category.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-3 h-3 rounded-full ${category.color}`}
                        />
                        <CardTitle className="text-lg">
                          {category.name}
                        </CardTitle>
                        <Badge variant="secondary">
                          {category.resources?.length || 0} resources
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openNewResourceModal(category.id)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Resource
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openEditCategoryModal(category)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Category
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setDeleteType("category");
                              setDeleteId(category.id);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Category
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent>
                      {category.description && (
                        <p className="text-sm text-muted-foreground mb-4">
                          {category.description}
                        </p>
                      )}
                      {category.resources && category.resources.length > 0 ? (
                        <div className="space-y-2">
                          {category.resources.map((resource) => (
                            <div
                              key={resource.id}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{resource.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {resource.fileType}
                                    {resource.fileSize &&
                                      ` • ${resource.fileSize}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!resource.isPublished && (
                                  <Badge variant="outline">Draft</Badge>
                                )}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() =>
                                        openEditResourceModal(resource)
                                      }
                                    >
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => {
                                        setDeleteType("resource");
                                        setDeleteId(resource.id);
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No resources in this category yet.</p>
                          <Button
                            variant="link"
                            onClick={() => openNewResourceModal(category.id)}
                          >
                            Add a resource
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="all-resources">
              <Card>
                <CardContent className="pt-6">
                  {resources.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Download className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>
                        No resources yet. Create a category first, then add
                        resources.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {resources.map((resource) => (
                        <div
                          key={resource.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{resource.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {resource.category?.name} • {resource.fileType}
                                {resource.fileSize && ` • ${resource.fileSize}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!resource.isPublished && (
                              <Badge variant="outline">Draft</Badge>
                            )}
                            {resource.externalUrl && (
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    openEditResourceModal(resource)
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => {
                                    setDeleteType("resource");
                                    setDeleteId(resource.id);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
